import React from 'react'
import Dropdown from '@mui/joy/Dropdown'
import type { InputProps } from '@mui/joy/Input'
import ListDivider from '@mui/joy/ListDivider'
import Menu from '@mui/joy/Menu'
import MenuButton from '@mui/joy/MenuButton'
import MenuItem from '@mui/joy/MenuItem'
import Box from '@mui/joy/Box'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { styled } from '@mui/joy/styles'
import { useTranslation } from 'react-i18next'
import type { CountryCode } from 'libphonenumber-js'

import FloatingLabelFieldShell from '../floating-label-field-shell'
import {
    formatInternationalPhoneValue,
    getPhoneCountryFlag,
    getPhoneCountryGroups,
    getNormalizedPhoneValue,
    getPhoneCountryFromInput,
    getPhoneCountryOptions,
    rewritePhoneCountryInInput,
} from './utils'

const StyledInput = styled('input')({
    border: 'none',
    minWidth: 0,
    width: '100%',
    outline: 0,
    padding: 0,
    paddingTop: '1em',
    flex: 1,
    color: 'inherit',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontStyle: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    textOverflow: 'ellipsis',
    '&::placeholder': {
        opacity: 0,
        transition: '0.1s ease-out',
    },
    '&:focus::placeholder': {
        opacity: 1,
    },
    '&::-webkit-contacts-auto-fill-button': {
        display: 'none !important',
        visibility: 'hidden',
        pointerEvents: 'none',
    },
})

const COUNTRY_MENU_WIDTH = 320

function isPrintableKey(event: React.KeyboardEvent<HTMLElement>): boolean {
    return event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey
}

function matchesTypeahead(
    option: { name: string; isoCode: string; callingCode: string },
    query: string
): boolean {
    const normalizedQuery = query.trim().toLowerCase()

    if (!normalizedQuery) {
        return false
    }

    return [option.name, option.isoCode, option.callingCode].some(value =>
        value.toLowerCase().startsWith(normalizedQuery)
    )
}

const StyledCountryButton = styled(MenuButton)({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    height: 'auto',
    minHeight: 28,
    border: 'none',
    borderRadius: 10,
    paddingInline: '8px',
    paddingBlock: '2px',
    // backgroundColor: 'var(--joy-palette-neutral-100)',
    font: 'inherit',
    color: 'inherit',
    fontWeight: 600,
    lineHeight: 1.2,
    '&:hover': {
        backgroundColor: 'var(--joy-palette-neutral-100)',
    },
    '&:focus-visible': {
        outline: 'none',
    },
})

type FloatingLabelPhoneInputProps = {
    label: string
    value: string
    defaultCountry: CountryCode
    required?: boolean
    error?: boolean
    id?: string
    name?: string
    inputRef?: React.Ref<HTMLInputElement>
    disabled?: boolean
    sx?: InputProps['sx']
    onChange: (value: string) => void
    onBlur?: () => void
    onCountryChange?: (country: CountryCode) => void
}

export default function FloatingLabelPhoneInput({
    label,
    value,
    defaultCountry,
    required = false,
    error = false,
    id,
    name,
    inputRef,
    disabled = false,
    sx,
    onChange,
    onBlur,
    onCountryChange,
}: FloatingLabelPhoneInputProps) {
    const { i18n } = useTranslation()

    const generatedId = React.useId()
    const inputId = id ?? generatedId

    const [isFocused, setIsFocused] = React.useState(false)
    const [isMenuOpen, setIsMenuOpen] = React.useState(false)

    const visibleInputRef = React.useRef<HTMLInputElement | null>(null)
    const handleVisibleInputRef = React.useCallback((element: HTMLInputElement | null) => {
        visibleInputRef.current = element

        if (typeof inputRef === 'function') {
            inputRef(element)
        } else if (inputRef) {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = element
        }
    }, [inputRef])
    const countryMenuRef = React.useRef<HTMLDivElement | null>(null)
    const typeaheadBufferRef = React.useRef('')
    const typeaheadTimeoutRef = React.useRef<number | null>(null)

    const currentCountry = getPhoneCountryFromInput(value, defaultCountry)
    const normalizedValue = getNormalizedPhoneValue(value, currentCountry)
    const countryOptions = React.useMemo(
        () => getPhoneCountryOptions(i18n.language),
        [i18n.language]
    )
    const countryGroups = React.useMemo(
        () => getPhoneCountryGroups(i18n.language),
        [i18n.language]
    )
    const selectedOption =
        countryOptions.find(option => option.isoCode === currentCountry) ?? countryOptions[0]

    const handleFieldBlur = React.useCallback(
        (event: React.FocusEvent<HTMLDivElement>) => {
            if (event.currentTarget.contains(event.relatedTarget)) {
                return
            }

            if (!isMenuOpen) {
                setIsFocused(false)
            }
        },
        [isMenuOpen]
    )

    const handleInputFocus = () => {
        setIsFocused(true)
    }

    const handleInputBlur = () => {
        if (isMenuOpen) {
            return
        }

        const nextNormalizedValue = getNormalizedPhoneValue(value, currentCountry)
        const nextDraftValue = formatInternationalPhoneValue(nextNormalizedValue) || value

        onChange(nextDraftValue)
        onBlur?.()
    }

    const handleVisibleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value)
    }

    const handleCountrySelect = (country: CountryCode) => {
        const nextDraftValue = rewritePhoneCountryInInput(value, country)

        onCountryChange?.(country)
        onChange(nextDraftValue)
        setIsMenuOpen(false)
        visibleInputRef.current?.focus()
    }

    const clearTypeahead = React.useCallback(() => {
        typeaheadBufferRef.current = ''

        if (typeaheadTimeoutRef.current !== null) {
            window.clearTimeout(typeaheadTimeoutRef.current)
            typeaheadTimeoutRef.current = null
        }
    }, [])

    const focusCountryOption = React.useCallback((country: CountryCode) => {
        const focusMatchingItem = () => {
            const menuItem = countryMenuRef.current?.querySelector<HTMLElement>(
                `[data-country-code="${country}"]`
            )

            menuItem?.focus()
        }

        if (isMenuOpen) {
            focusMatchingItem()
            return
        }

        setIsMenuOpen(true)
        window.requestAnimationFrame(focusMatchingItem)
    }, [isMenuOpen])

    const getTypeaheadCountry = React.useCallback(
        (query: string, startCountry?: CountryCode): CountryCode | null => {
            if (!countryOptions.length) {
                return null
            }

            const startIndex = startCountry
                ? countryOptions.findIndex(option => option.isoCode === startCountry)
                : -1

            for (let offset = 1; offset <= countryOptions.length; offset += 1) {
                const option = countryOptions[(startIndex + offset) % countryOptions.length]

                if (option && matchesTypeahead(option, query)) {
                    return option.isoCode
                }
            }

            return null
        },
        [countryOptions]
    )

    const handleCountryTypeahead = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (!isPrintableKey(event)) {
                return
            }

            event.preventDefault()
            event.stopPropagation()

            const typedCharacter = event.key.toLowerCase()
            const nextBuffer = `${typeaheadBufferRef.current}${typedCharacter}`
            const repeatedCharacter = nextBuffer.split('').every(character => character === typedCharacter)
            const query = repeatedCharacter ? typedCharacter : nextBuffer
            const activeElement = document.activeElement as HTMLElement | null
            const activeCountry =
                activeElement?.dataset.countryCode as CountryCode | undefined
            const matchedCountry = getTypeaheadCountry(query, activeCountry ?? currentCountry)

            typeaheadBufferRef.current = query

            if (typeaheadTimeoutRef.current !== null) {
                window.clearTimeout(typeaheadTimeoutRef.current)
            }

            typeaheadTimeoutRef.current = window.setTimeout(() => {
                typeaheadBufferRef.current = ''
                typeaheadTimeoutRef.current = null
            }, 500)

            if (matchedCountry) {
                focusCountryOption(matchedCountry)
            }
        },
        [currentCountry, focusCountryOption, getTypeaheadCountry]
    )

    return (
        <Box onFocusCapture={() => setIsFocused(true)} onBlurCapture={handleFieldBlur}>
            <FloatingLabelFieldShell
                label={label}
                required={required}
                htmlFor={inputId}
                focused={isFocused || isMenuOpen}
                filled={value !== ''}
                error={error}
                disabled={disabled}
                startDecorator={
                    isFocused || value !== '' ? (
                        <Dropdown
                            open={isMenuOpen}
                            onOpenChange={(_event, open) => {
                                setIsMenuOpen(open)

                                if (!open) {
                                    clearTypeahead()
                                }
                            }}
                        >
                            <StyledCountryButton
                                disabled={disabled}
                                aria-label={`${label} country`}
                                onMouseDown={event => event.preventDefault()}
                                onKeyDownCapture={handleCountryTypeahead}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{ fontSize: '1rem', lineHeight: 1 }}
                                >
                                    {getPhoneCountryFlag(selectedOption?.isoCode ?? currentCountry)}
                                </span>
                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                            </StyledCountryButton>
                            <Menu
                                ref={countryMenuRef}
                                placement="bottom-start"
                                onKeyDownCapture={handleCountryTypeahead}
                                sx={{
                                    width: COUNTRY_MENU_WIDTH,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                    zIndex: 1400,
                                }}
                            >
                                {countryGroups.map((group, groupIndex) => (
                                    <React.Fragment key={group.id}>
                                        {groupIndex > 0 ? <ListDivider inset="gutter" /> : null}
                                        <Box
                                            sx={{
                                                px: 2,
                                                pt: groupIndex > 0 ? 1.25 : 1,
                                                pb: 0.5,
                                                fontSize: '0.7rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                color: 'text.tertiary',
                                            }}
                                        >
                                            {group.label}
                                        </Box>
                                        {group.options.map(option => (
                                            <MenuItem
                                                key={option.isoCode}
                                                data-country-code={option.isoCode}
                                                selected={option.isoCode === currentCountry}
                                                onClick={() => handleCountrySelect(option.isoCode)}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        minWidth: 0,
                                                        width: '100%',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box
                                                        aria-hidden="true"
                                                        sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 20 }}
                                                    >
                                                        {getPhoneCountryFlag(option.isoCode)}
                                                    </Box>
                                                    <Box sx={{ fontWeight: 600, minWidth: 54 }}>
                                                        {option.callingCode}
                                                    </Box>
                                                    <Box sx={{ color: 'text.secondary', minWidth: 32 }}>
                                                        {option.isoCode}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {option.name}
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Menu>
                        </Dropdown>
                    ) : null
                }
                sx={sx}
            >
                <StyledInput
                    ref={handleVisibleInputRef}
                    id={inputId}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder=" "
                    value={value}
                    disabled={disabled}
                    required={required}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onChange={handleVisibleInputChange}
                />
                {name && <input type="hidden" name={name} value={normalizedValue} />}
            </FloatingLabelFieldShell>
        </Box>
    )
}
